/**
 * Resolves a safe in-app path after login. Blocks open redirects and auth pages.
 */

const DEFAULT_AFTER_LOGIN = "/home";

const BLOCKED_PATH_PREFIXES = ["/login", "/breach", "/auth/activate", "/auth/reset-password"];

function isBlockedPath(pathname) {
  return BLOCKED_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/**
 * @param {string} fullPath pathname + search (+ optional hash)
 * @returns {string|null}
 */
export function getSafeReturnPath(fullPath) {
  if (!fullPath || typeof fullPath !== "string") return null;
  const trimmed = fullPath.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  try {
    const url = new URL(trimmed, "http://local.invalid");
    const path = url.pathname;
    if (isBlockedPath(path)) return null;
    if (trimmed.length > 2048) return null;
    return path + url.search + url.hash;
  } catch {
    return null;
  }
}

/**
 * @param {import('react-router-dom').Location | { pathname?: string, search?: string }} from
 * @returns {string|null}
 */
export function getSafeReturnPathFromLocation(from) {
  if (!from?.pathname) return null;
  return getSafeReturnPath(`${from.pathname}${from.search || ""}${from.hash || ""}`);
}

/**
 * Prefer router state.from (set when kicking to login), then optional ?from= query.
 * @param {import('react-router-dom').Location} location
 * @returns {string}
 */
export function resolvePostLoginDestination(location) {
  const fromState = getSafeReturnPathFromLocation(location?.state?.from);
  if (fromState) return fromState;

  const param = new URLSearchParams(location?.search || "").get("from");
  if (param) {
    const fromQuery = getSafeReturnPath(param);
    if (fromQuery) return fromQuery;
  }

  return DEFAULT_AFTER_LOGIN;
}
